import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/use-auth";
import { getLocale, setLocale, t } from "../i18n";
import { NAV_ITEMS, type NavItem } from "./admin-navigation";
import { sidebarStyles as styles } from "./sidebar-styles";

export function Sidebar({
  collapsed,
  onCollapsedChange,
}: {
  readonly collapsed: boolean;
  readonly onCollapsedChange: (collapsed: boolean) => void;
}): React.JSX.Element {
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <aside
      style={{
        ...styles.sidebar,
        width: collapsed
          ? "var(--sidebar-collapsed-width)"
          : "var(--sidebar-width)",
      }}
    >
      <SidebarHeader
        collapsed={collapsed}
        onCollapsedChange={onCollapsedChange}
      />
      <nav style={styles.nav}>
        {NAV_ITEMS.map((item) => (
          <NavGroup
            key={item.labelKey}
            item={item}
            collapsed={collapsed}
            pathname={location.pathname}
          />
        ))}
      </nav>
      <SidebarFooter
        collapsed={collapsed}
        userEmail={user?.email}
        onLogout={logout}
      />
    </aside>
  );
}

function SidebarHeader({
  collapsed,
  onCollapsedChange,
}: {
  readonly collapsed: boolean;
  readonly onCollapsedChange: (collapsed: boolean) => void;
}): React.JSX.Element {
  const label = collapsed ? t("sidebar.expand_nav") : t("sidebar.collapse_nav");
  return (
    <div style={styles.logo}>
      {!collapsed && <span>{t("sidebar.logo")}</span>}
      <button
        type="button"
        onClick={() => onCollapsedChange(!collapsed)}
        style={styles.collapseBtn}
        title={label}
        aria-label={label}
      >
        {collapsed ? "\u203A" : "\u2039"}
      </button>
    </div>
  );
}

function SidebarFooter({
  collapsed,
  userEmail,
  onLogout,
}: {
  readonly collapsed: boolean;
  readonly userEmail?: string;
  readonly onLogout: () => void;
}): React.JSX.Element {
  const localeLabel = getLocale() === "en" ? "\u4E2D\u6587" : "English";
  return (
    <div style={styles.footer}>
      {!collapsed && <div style={styles.userEmail}>{userEmail ?? ""}</div>}
      <button
        type="button"
        onClick={onLogout}
        style={collapsed ? styles.compactFooterBtn : styles.logoutBtn}
        title={t("sidebar.sign_out")}
        aria-label={t("sidebar.sign_out")}
      >
        {collapsed ? "\u21AA" : t("sidebar.sign_out")}
      </button>
      <button
        type="button"
        onClick={() => setLocale(getLocale() === "en" ? "zh" : "en")}
        style={collapsed ? styles.compactFooterBtn : styles.localeBtn}
        title={localeLabel}
        aria-label={localeLabel}
      >
        {collapsed ? "\u6587" : localeLabel}
      </button>
    </div>
  );
}

function NavGroup({
  item,
  collapsed,
  pathname,
}: {
  readonly item: NavItem;
  readonly collapsed: boolean;
  readonly pathname: string;
}): React.JSX.Element {
  if (item.path !== undefined) {
    return <TopLevelNavLink item={item} collapsed={collapsed} />;
  }

  const isActiveGroup =
    item.children?.some((child) => pathname.startsWith(child.path)) ?? false;

  return (
    <div>
      <div
        style={{
          ...(collapsed ? styles.compactNavGroupLabel : styles.navGroupLabel),
          ...(isActiveGroup ? styles.navLinkActive : {}),
        }}
        title={t(item.labelKey)}
        aria-label={t(item.labelKey)}
      >
        <NavLabel
          icon={item.icon}
          label={t(item.labelKey)}
          collapsed={collapsed}
        />
      </div>
      {!collapsed && item.children && (
        <div style={styles.navChildren}>
          {item.children.map((child) => (
            <NavLink
              key={child.path}
              to={child.path}
              style={({ isActive }) => ({
                ...styles.navChildLink,
                ...(isActive ? styles.navLinkActive : {}),
              })}
            >
              {t(child.labelKey)}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function TopLevelNavLink({
  item,
  collapsed,
}: {
  readonly item: NavItem;
  readonly collapsed: boolean;
}): React.JSX.Element {
  return (
    <NavLink
      to={item.path ?? "/"}
      end
      title={t(item.labelKey)}
      style={({ isActive }) => ({
        ...(collapsed ? styles.compactNavLink : styles.navLink),
        ...(isActive ? styles.navLinkActive : {}),
      })}
    >
      <NavLabel
        icon={item.icon}
        label={t(item.labelKey)}
        collapsed={collapsed}
      />
    </NavLink>
  );
}

function NavLabel({
  icon,
  label,
  collapsed,
}: {
  readonly icon: string;
  readonly label: string;
  readonly collapsed: boolean;
}): React.JSX.Element {
  return (
    <span style={styles.navContent}>
      <span style={styles.navIcon} aria-hidden="true">
        {icon}
      </span>
      {!collapsed && <span style={styles.navText}>{label}</span>}
    </span>
  );
}
